import type { ReactNode } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  DocumentViewer,
  type DocumentViewerKind,
  type PdfHighlight,
  type XlsxRowHighlight,
} from "@volca/ui/components/ui/document-viewer";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { cn } from "@volca/ui/lib/utils";
import type { ProfitabilityDocument } from "@/profitability/types";
import { reportError } from "@/profitability/utils";
import type { DocumentCitation } from "./citation";
import { useDocumentContent, workbookRefusal } from "./query";
import { isSpreadsheet } from "./views";

// A fixed height on a narrow screen, where the page scrolls; the rest of the
// tab on a wide one, where the tab fills the page.
const VIEWER_CLASS = "h-128 lg:h-auto lg:min-h-0 lg:flex-1";

/** "jobs.csv" → "jobs.xlsx": the name of a CSV's workbook. */
const workbookName = (fileName: string) =>
  `${fileName.replace(/\.[^.]+$/, "")}.xlsx`;

/**
 * The file as it was uploaded, drawn by the viewer for its kind (a PDF's
 * pages, a workbook's sheets, a Word document, an image), whether or not it
 * could be read. A CSV or TSV is drawn by the same spreadsheet viewer, from
 * the API's .xlsx conversion of it; one too large to convert shows
 * `fallback` (its rows as read) instead. A spreadsheet has a chip per column
 * with blank cells. A citation opens it at the figure: a PDF's page and box
 * (and its words searched), a spreadsheet's cell and row.
 */
export const DocumentOriginal = ({
  businessId,
  document,
  kind,
  citation,
  highlightLabel,
  highlights,
  rows,
  fallback,
  className = VIEWER_CLASS,
}: {
  businessId: string;
  document: Pick<ProfitabilityDocument, "id" | "file_name">;
  kind: DocumentViewerKind;
  citation: DocumentCitation | null;
  /** Names the PDF's highlighted box ("Row 13"). */
  highlightLabel?: string;
  /**
   * A PDF's boxes, when a figure is made of several rows: in place of the
   * citation's one box. The page still comes from the citation.
   */
  highlights?: PdfHighlight[];
  /** A spreadsheet's rows to tint beside the citation's cell. */
  rows?: XlsxRowHighlight[];
  /** A CSV the API will not convert: what to show instead, and why. */
  fallback?: (reason: string) => ReactNode;
  /** Sizes the viewer; by default the Documents tab's. */
  className?: string;
}) => {
  const workbook = kind === "csv";
  const content = useDocumentContent(
    businessId,
    document.id,
    workbook ? "workbook" : "content",
  );

  if (content.isPending)
    return <Skeleton className={cn(className, "rounded-xl")} />;
  const refused = workbook ? workbookRefusal(content.error) : null;
  if (refused !== null && fallback) return fallback(refused);
  if (content.isError)
    return (
      <ErrorState
        illustration="compact"
        title="This file could not be loaded"
        description={reportError(content.error)}
        retrying={content.isFetching}
      >
        <Button onClick={() => void content.refetch()}>Try again</Button>
      </ErrorState>
    );

  const { page, box, sheet, row, column, find } = citation ?? {};
  return (
    <DocumentViewer
      kind={kind}
      data={workbook ? undefined : content.data}
      workbook={
        workbook
          ? { data: content.data, fileName: workbookName(document.file_name) }
          : undefined
      }
      fileName={document.file_name}
      className={className}
      page={page}
      highlights={
        highlights ??
        (page && box
          ? [{ page, box, tone: "info", label: highlightLabel }]
          : undefined)
      }
      searchText={find}
      activeSheet={sheet}
      selectedCell={row && column ? { sheet, row, column } : undefined}
      highlightRow
      highlightRows={rows}
      blanks={isSpreadsheet(kind)}
    />
  );
};
